/**
 * Single notification row in the tray. Unread rows get a light orange
 * tint background + bold title + orange dot — matches the screenshot
 * reference from product. Click marks the row as read and navigates to
 * `target_url` if present and safe.
 */
import React from 'react';
import { Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { snakeToTitleCase } from '@/modules/shared/utils';
import { useAppStore } from '@/modules/shared/store/appStore';
import type { NotificationLike } from './useNotificationStream';
import { testIds } from '@/test/testIds';

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

interface Props {
  notification: NotificationLike;
  onClick: (n: NotificationLike) => void;
}

// ---- assignment rows (Figma: Quotation/Lead <id> + VMI/CMI tag + by <name> • <time>) ----

interface AssignmentDisplay {
  title: string;
  /** VMI/CMI product tag derived from extra_params.policy_type. */
  productTag: string | null;
  /** Title-cased extra_params.status, e.g. pending → Pending. */
  status: string | null;
  /** extra_params.assigned_by, falling back to the current user's name. */
  assignedBy: string | null;
  /** extra_params.assigned_at, falling back to the notification created_at. */
  timestamp: string | null | undefined;
}

const idOrNull = (v: unknown): string | number | null =>
  typeof v === 'number' || (typeof v === 'string' && v.trim()) ? (v as string | number) : null;

/** SSE frames carry `created_at`; the REST inbox shape carries `date`. */
const notificationTimestamp = (n: NotificationLike): string | null | undefined => n.created_at ?? n.date;

/**
 * Build the assignment-specific display model, or null when the payload
 * doesn't carry usable extra_params (→ caller falls back to the generic row).
 */
const assignmentDisplay = (n: NotificationLike, currentUserName: string | null): AssignmentDisplay | null => {
  if (n.subcategory !== 'assignment') return null;
  const extra = n.extra_params ?? {};

  // Quotation wins when present; otherwise it's a lead-level assignment.
  const quotationId = idOrNull(extra.quotation_id);
  const leadId = idOrNull(extra.lead_id);
  if (quotationId == null && leadId == null) return null;
  const title = quotationId != null ? `Quotation ${quotationId}` : `Lead ${leadId}`;

  const policyType = typeof extra.policy_type === 'string' ? extra.policy_type.toLowerCase() : '';
  const productTag = policyType === 'vmi' ? 'VMI' : policyType === 'cmi' ? 'CMI' : null;

  const status = typeof extra.status === 'string' && extra.status.trim() ? snakeToTitleCase(extra.status) : null;

  const assignedBy =
    typeof extra.assigned_by === 'string' && extra.assigned_by.trim() ? extra.assigned_by.trim() : currentUserName;

  const timestamp =
    typeof extra.assigned_at === 'string' && extra.assigned_at ? extra.assigned_at : notificationTimestamp(n);

  return { title, productTag, status, assignedBy, timestamp };
};

// ---- mention rows (Figma: <EntryType> on <Policy|Lead> <id> + VMI tag + 📎n, preview, by <name> • <time>) ----

/** extra_params.entry_type → display noun for the mention title. */
const MENTION_ENTRY_LABELS: Record<string, string> = {
  remark: 'Remarks',
  reply: 'Reply',
  rework: 'Rework',
};

interface MentionDisplay {
  title: string;
  /** VMI/CMI product tag derived from extra_params.policy_type. */
  productTag: string | null;
  /** extra_params.attachment_count when > 0, else null (chip hidden). */
  attachmentCount: number | null;
  /** extra_params.mention_text_preview (falls back to the notification content). */
  preview: string | null;
  mentionedBy: string | null;
  timestamp: string | null | undefined;
}

/**
 * Build the mention-specific display model, or null when the payload doesn't
 * carry usable extra_params (→ caller falls back to the generic row).
 */
const mentionDisplay = (n: NotificationLike): MentionDisplay | null => {
  if (n.subcategory !== 'mention') return null;
  const extra = n.extra_params ?? {};

  const quotationId = idOrNull(extra.quotation_id);
  const leadId = idOrNull(extra.lead_id);
  if (quotationId == null && leadId == null) return null;

  const entryType = typeof extra.entry_type === 'string' ? extra.entry_type.toLowerCase() : '';
  const entryLabel = MENTION_ENTRY_LABELS[entryType] ?? (entryType ? snakeToTitleCase(entryType) : 'Mention');
  const target = quotationId != null ? `Policy ${quotationId}` : `Lead ${leadId}`;

  const policyType = typeof extra.policy_type === 'string' ? extra.policy_type.toLowerCase() : '';
  const productTag = policyType === 'vmi' ? 'VMI' : policyType === 'cmi' ? 'CMI' : null;

  const attachmentCount =
    typeof extra.attachment_count === 'number' && extra.attachment_count > 0 ? extra.attachment_count : null;

  const preview =
    typeof extra.mention_text_preview === 'string' && extra.mention_text_preview.trim()
      ? extra.mention_text_preview
      : n.content || null;

  // `mentioned_by` is a user id today; the sender's display name lives in the
  // "<name> mentioned you" title, so prefer that over echoing a numeric id.
  let mentionedBy: string | null = null;
  if (typeof extra.mentioned_by === 'string' && extra.mentioned_by.trim()) {
    mentionedBy = extra.mentioned_by.trim();
  } else if (typeof n.title === 'string' && n.title.endsWith(' mentioned you')) {
    mentionedBy = n.title.slice(0, -' mentioned you'.length).trim() || null;
  }

  return {
    title: `${entryLabel} on ${target}`,
    productTag,
    attachmentCount,
    preview,
    mentionedBy,
    timestamp: notificationTimestamp(n),
  };
};

const NotificationItem: React.FC<Props> = ({ notification, onClick }) => {
  const isUnread = notification.is_unread !== false;
  const currentUserName = useAppStore((s) => {
    const fullname = (s.user as { fullname?: unknown } | null)?.fullname;
    return typeof fullname === 'string' && fullname.trim() ? fullname.trim() : null;
  });
  const assignment = assignmentDisplay(notification, currentUserName);
  const mention = mentionDisplay(notification);

  if (mention) {
    return (
      <button
        type="button"
        onClick={() => onClick(notification)}
        data-testid={testIds.notificationsTray.item(notification.id)}
        className={`flex w-full flex-col gap-1 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-accent ${
          isUnread ? 'bg-primary/10/60' : 'bg-background'
        }`}
      >
        <div className="flex items-start gap-2">
          {isUnread ? (
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />
          ) : (
            <span className="mt-1.5 h-2 w-2 shrink-0" aria-hidden="true" />
          )}
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-sm ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                {mention.title}
              </span>
              {mention.productTag && (
                <Badge variant="outline" className="text-2xs uppercase leading-none">
                  {mention.productTag}
                </Badge>
              )}
              {mention.attachmentCount != null && (
                <Badge variant="outline" className="gap-1 text-2xs leading-none">
                  <Paperclip className="h-3 w-3 text-primary" />
                  {mention.attachmentCount}
                </Badge>
              )}
            </div>
            {mention.preview && (
              <div className="line-clamp-2 break-words text-xs text-muted-foreground">{mention.preview}</div>
            )}
            <div className="flex flex-wrap items-center gap-1.5 pt-1 text-caption text-muted-foreground">
              {mention.mentionedBy && (
                <>
                  <span className="font-medium text-primary">by {mention.mentionedBy}</span>
                  <span aria-hidden="true">•</span>
                </>
              )}
              <span>{formatRelative(mention.timestamp)}</span>
            </div>
          </div>
        </div>
      </button>
    );
  }

  if (assignment) {
    return (
      <button
        type="button"
        onClick={() => onClick(notification)}
        data-testid={testIds.notificationsTray.item(notification.id)}
        className={`flex w-full flex-col gap-1 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-accent ${
          isUnread ? 'bg-primary/10/60' : 'bg-background'
        }`}
      >
        <div className="flex items-start gap-2">
          {isUnread ? (
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />
          ) : (
            <span className="mt-1.5 h-2 w-2 shrink-0" aria-hidden="true" />
          )}
          <div className="flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-sm ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                {assignment.title}
              </span>
              {assignment.productTag && (
                <Badge variant="outline" className="text-2xs uppercase leading-none">
                  {assignment.productTag}
                </Badge>
              )}
              {assignment.status && (
                <Badge variant="outline" className="text-2xs leading-none">
                  {assignment.status}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 pt-1 text-caption text-muted-foreground">
              {assignment.assignedBy && (
                <>
                  <span className="font-medium text-primary">by {assignment.assignedBy}</span>
                  <span aria-hidden="true">•</span>
                </>
              )}
              <span>{formatRelative(assignment.timestamp)}</span>
            </div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      data-testid={testIds.notificationsTray.item(notification.id)}
      className={`flex w-full flex-col gap-1 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-accent ${
        isUnread ? 'bg-primary/10/60' : 'bg-background'
      }`}
    >
      <div className="flex items-start gap-2">
        {isUnread ? (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />
        ) : (
          <span className="mt-1.5 h-2 w-2 shrink-0" aria-hidden="true" />
        )}
        <div className="flex-1 space-y-0.5">
          <div
            className={`line-clamp-2 text-sm ${
              isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'
            }`}
          >
            {notification.title || '(untitled)'}
          </div>
          {notification.content && (
            <div className="line-clamp-2 text-xs text-muted-foreground">{notification.content}</div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1 text-caption text-muted-foreground">
            <span>{formatRelative(notification.created_at)}</span>
            {notification.subcategory && notification.subcategory !== 'general' ? (
              <Badge
                variant="outline"
                className="border-primary/30 bg-primary/10 text-2xs uppercase leading-none text-primary"
              >
                {notification.subcategory}
              </Badge>
            ) : null}
            {notification.tags?.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-2xs uppercase leading-none">
                {tag}
              </Badge>
            ))}
            {notification.config_identifier && (
              <Badge variant="outline" className="text-2xs leading-none">
                {notification.config_identifier}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default NotificationItem;
